import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Header } from "@/components/Header";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { 
  Calendar, Clock, Video, Users, TrendingUp, Briefcase, Building2,
  Phone, Mail, CheckCircle, Loader2, ArrowRight, Star, Zap,
  Sparkles, Play, Trophy, Target, Rocket, Globe, Timer
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PitchDayPage = () => {
  const { user, token } = useAuth();
  const { toast } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [investors, setInvestors] = useState([]);
  const [startups, setStartups] = useState([]);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedInvestor, setSelectedInvestor] = useState(null);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [myBookings, setMyBookings] = useState([]);
  const [activeTab, setActiveTab] = useState('investors');
  
  const [bookingForm, setBookingForm] = useState({
    startup_name: '',
    pitch_summary: '',
    booking_date: '',
    time_slot: '',
    contact_email: user?.email || ''
  });
  
  const [availableDates, setAvailableDates] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);

  useEffect(() => {
    fetchData();
    fetchAvailableSlots();
  }, []);

  const fetchAvailableSlots = async () => {
    try {
      const response = await axios.get(`${API}/pitch-bookings/available-slots`);
      setAvailableDates(response.data.dates || []);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error("Error fetching slots:", error);
    }
  };

  const fetchSlotsForDate = async (date) => {
    try {
      const response = await axios.get(`${API}/pitch-bookings/available-slots?date=${date}`);
      setAvailableSlots(response.data.slots || []);
    } catch (error) {
      console.error("Error fetching slots for date:", error);
    }
  };

  const fetchData = async () => {
    try {
      const [investorsRes, startupsRes] = await Promise.all([
        axios.get(`${API}/investors/public`),
        axios.get(`${API}/startups`)
      ]);
      setInvestors(investorsRes.data);
      setStartups(startupsRes.data.slice(0, 12));
      
      if (token) {
        try {
          const bookingsRes = await axios.get(`${API}/pitch-bookings/me`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          setMyBookings(bookingsRes.data);
        } catch (bookErr) {
          // Non-fatal: user may not have bookings yet, or token may be stale.
          console.warn('[PitchDayPage] my-bookings fetch failed:', bookErr?.response?.status || bookErr?.message);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    }
    setLoading(false);
  };

  const handleBookCall = async () => {
    if (!bookingForm.startup_name || !bookingForm.booking_date || !bookingForm.time_slot) {
      toast({ title: "Required", description: "Please fill all required fields", variant: "destructive" });
      return;
    }
    
    setBookingLoading(true);
    try {
      const response = await axios.post(`${API}/pitch-bookings`, {
        ...bookingForm,
        investor_id: selectedInvestor?.id
      }, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
      
      toast({ 
        title: "🎉 Pitch Booked!", 
        description: `Your pitch is confirmed for ${new Date(bookingForm.booking_date).toLocaleDateString()} at ${bookingForm.time_slot}` 
      });
      setBookingOpen(false);
      setBookingForm({ startup_name: '', pitch_summary: '', booking_date: '', time_slot: '', contact_email: user?.email || '' });
      fetchData();
    } catch (error) {
      toast({ title: "Error", description: error.response?.data?.detail || "Booking failed", variant: "destructive" });
    }
    setBookingLoading(false);
  };

  const nextPitchDay = availableDates[0] ? new Date(availableDates[0]) : new Date();

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />
      
      {/* Hero Section with Animation */}
      <div className="relative overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-br from-violet-900/30 via-blue-900/20 to-emerald-900/30" />
        <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-violet-500/20 rounded-full blur-[120px] animate-pulse" />
        <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-emerald-500/20 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        
        {/* Floating Elements */}
        <div className="absolute top-20 right-20 text-violet-400/20 animate-bounce" style={{ animationDuration: '3s' }}>
          <Rocket className="h-16 w-16" />
        </div>
        <div className="absolute bottom-40 left-20 text-emerald-400/20 animate-bounce" style={{ animationDuration: '4s', animationDelay: '0.5s' }}>
          <Trophy className="h-12 w-12" />
        </div>
        
        <div className="container mx-auto px-6 py-20 relative">
          <div className="max-w-4xl mx-auto text-center">
            {/* Live Badge */}
            <div className="inline-flex items-center gap-2 bg-red-500/20 border border-red-500/50 rounded-full px-4 py-2 mb-8 animate-pulse">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-ping" />
              <span className="text-red-400 font-medium text-sm">LIVE PITCH SESSIONS</span>
            </div>
            
            <h1 className="text-6xl font-black text-white mb-6 leading-tight">
              <span className="bg-gradient-to-r from-violet-400 via-blue-400 to-emerald-400 bg-clip-text text-transparent">
                Pitch Day
              </span>
              <br />
              <span className="text-4xl font-light text-gray-300">& Investor Suite</span>
            </h1>
            
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Get 15 minutes face-to-face with top investors. Every Friday, founders pitch live. 
              <span className="text-emerald-400"> First come, first served.</span>
            </p>
            
            {/* Stats Row */}
            <div className="flex items-center justify-center gap-8 mb-10">
              {[
                { icon: Calendar, value: "Every Friday", label: "Pitch Day" },
                { icon: Clock, value: "10AM - 4PM", label: "GST Time" },
                { icon: Users, value: `${investors.length}+`, label: "Active Investors" },
                { icon: Video, value: "15 min", label: "Per Pitch" },
              ].map((stat, i) => (
                <div key={i} className="text-center group">
                  <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-2xl bg-white/5 border border-white/10 group-hover:border-emerald-500/50 group-hover:bg-emerald-500/10 transition-all">
                    <stat.icon className="h-6 w-6 text-gray-400 group-hover:text-emerald-400 transition-colors" />
                  </div>
                  <div className="text-white font-bold">{stat.value}</div>
                  <div className="text-gray-500 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* CTA Button */}
            <Button 
              size="lg" 
              className="bg-gradient-to-r from-violet-600 to-emerald-600 hover:from-violet-700 hover:to-emerald-700 text-white px-8 py-6 text-lg rounded-2xl shadow-lg shadow-violet-500/25 group"
              onClick={() => document.getElementById('investors-section').scrollIntoView({ behavior: 'smooth' })}
            >
              <Zap className="h-5 w-5 mr-2 group-hover:animate-pulse" />
              Book Your Pitch Slot
              <ArrowRight className="h-5 w-5 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>

      {/* Countdown Timer */}
      <div className="bg-gradient-to-r from-violet-900/50 to-emerald-900/50 border-y border-white/10">
        <div className="container mx-auto px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/10 rounded-xl">
                <Timer className="h-6 w-6 text-emerald-400" />
              </div>
              <div>
                <p className="text-gray-400 text-sm">Next Pitch Day</p>
                <p className="text-white font-bold text-lg">
                  {nextPitchDay.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              {availableSlots.slice(0, 5).map((slot, i) => (
                <div key={i} className={`text-center px-4 py-2 rounded-xl ${slot.available > 0 ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-red-500/20 border border-red-500/30'}`}>
                  <p className={`font-bold ${slot.available > 0 ? 'text-emerald-400' : 'text-red-400'}`}>{slot.label?.split(' ')[0]}</p>
                  <p className={`text-xs ${slot.available > 0 ? 'text-emerald-400/70' : 'text-red-400/70'}`}>
                    {slot.available > 0 ? `${slot.available} left` : 'Full'}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-6 py-12" id="investors-section">
        {/* Tabs */}
        <div className="flex items-center gap-4 mb-8">
          {[
            { id: 'investors', label: 'Investor Network', icon: TrendingUp },
            { id: 'startups', label: 'Featured Startups', icon: Rocket },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all ${
                activeTab === tab.id 
                  ? 'bg-gradient-to-r from-violet-600 to-emerald-600 text-white shadow-lg' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-white/10'
              }`}
            >
              <tab.icon className="h-5 w-5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Investors Grid */}
        {activeTab === 'investors' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {loading ? (
              <div className="col-span-3 text-center py-20">
                <Loader2 className="h-10 w-10 animate-spin mx-auto text-violet-500" />
                <p className="text-gray-500 mt-4">Loading investors...</p>
              </div>
            ) : investors.length === 0 ? (
              <div className="col-span-3 text-center py-20">
                <Users className="h-16 w-16 mx-auto text-gray-700 mb-4" />
                <p className="text-gray-500">No investors available yet</p>
              </div>
            ) : (
              investors.map((inv, idx) => (
                <Card key={inv.id} className="bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-violet-500/50 transition-all group overflow-hidden">
                  <CardContent className="p-6">
                    {/* Investor Type Badge */}
                    <div className="flex items-center justify-between mb-4">
                      <Badge className={`${
                        inv.investor_type === 'Angel Investor' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                        inv.investor_type === 'Venture Capital' ? 'bg-violet-500/20 text-violet-400 border-violet-500/30' :
                        'bg-blue-500/20 text-blue-400 border-blue-500/30'
                      }`}>
                        {inv.investor_type || 'Investor'}
                      </Badge>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-amber-400 fill-amber-400" />
                        <span className="text-amber-400 text-sm font-medium">4.9</span>
                      </div>
                    </div>
                    
                    {/* Investor Info */}
                    <div className="flex items-start gap-4 mb-4">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-violet-500 to-emerald-500 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                        {inv.name?.charAt(0) || 'I'}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-bold text-white group-hover:text-emerald-400 transition-colors">
                          {inv.name}
                        </h3>
                        <p className="text-gray-400 text-sm">{inv.firm || inv.company || 'Independent'}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <Globe className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-500 text-xs">{inv.location || 'Global'}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Focus Areas */}
                    {inv.sectors && inv.sectors.length > 0 && (
                      <div className="flex flex-wrap gap-2 mb-4">
                        {inv.sectors.slice(0, 3).map((s, i) => (
                          <span key={i} className="text-xs bg-white/10 text-gray-300 px-3 py-1 rounded-full">
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    
                    {/* Investment Range */}
                    <div className="flex items-center justify-between p-3 bg-black/30 rounded-xl mb-4">
                      <span className="text-gray-500 text-sm">Ticket Size</span>
                      <span className="text-emerald-400 font-bold">{inv.investment_range || '$50K - $500K'}</span>
                    </div>
                    
                    {/* Request Button */}
                    <Dialog open={bookingOpen && selectedInvestor?.id === inv.id} onOpenChange={(open) => { setBookingOpen(open); if (open) setSelectedInvestor(inv); }}>
                      <DialogTrigger asChild>
                        <Button className="w-full bg-gradient-to-r from-violet-600 to-emerald-600 hover:from-violet-700 hover:to-emerald-700 group">
                          <Video className="h-4 w-4 mr-2" />
                          Request Pitch Call
                          <ArrowRight className="h-4 w-4 ml-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md">
                        <DialogHeader>
                          <DialogTitle className="text-2xl flex items-center gap-3">
                            <div className="p-2 bg-violet-500/20 rounded-xl">
                              <Video className="h-5 w-5 text-violet-400" />
                            </div>
                            Book Pitch with {inv.name}
                          </DialogTitle>
                          <DialogDescription className="text-gray-400">
                            15-minute live pitch session. Make it count!
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 mt-4">
                          <div>
                            <label className="text-sm font-medium text-gray-300">Startup Name *</label>
                            <Input 
                              value={bookingForm.startup_name}
                              onChange={(e) => setBookingForm({...bookingForm, startup_name: e.target.value})}
                              placeholder="Your startup"
                              className="bg-white/5 border-white/10 text-white mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-300">Email *</label>
                            <Input 
                              type="email"
                              value={bookingForm.contact_email}
                              onChange={(e) => setBookingForm({...bookingForm, contact_email: e.target.value})}
                              placeholder="you@startup.com"
                              className="bg-white/5 border-white/10 text-white mt-1"
                            />
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-300">Select Date *</label>
                            <Select value={bookingForm.booking_date} onValueChange={(v) => { 
                              setBookingForm({...bookingForm, booking_date: v, time_slot: ''});
                              fetchSlotsForDate(v);
                            }}>
                              <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                <SelectValue placeholder="Choose a date" />
                              </SelectTrigger>
                              <SelectContent className="bg-slate-800 border-white/10">
                                {availableDates.map(date => (
                                  <SelectItem key={date} value={date} className="text-white hover:bg-white/10">
                                    {new Date(date).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          {bookingForm.booking_date && (
                            <div>
                              <label className="text-sm font-medium text-gray-300">Select Time *</label>
                              <Select value={bookingForm.time_slot} onValueChange={(v) => setBookingForm({...bookingForm, time_slot: v})}>
                                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-1">
                                  <SelectValue placeholder="Choose time" />
                                </SelectTrigger>
                                <SelectContent className="bg-slate-800 border-white/10">
                                  {availableSlots.map(slot => (
                                    <SelectItem key={slot.time} value={slot.time} disabled={!slot.is_available} className="text-white hover:bg-white/10">
                                      {slot.label} {slot.is_available ? `(${slot.available} left)` : '(Full)'}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                          <div>
                            <label className="text-sm font-medium text-gray-300">Pitch Summary</label>
                            <Textarea 
                              value={bookingForm.pitch_summary}
                              onChange={(e) => setBookingForm({...bookingForm, pitch_summary: e.target.value})}
                              placeholder="What does your startup do? (2-3 sentences)"
                              className="bg-white/5 border-white/10 text-white mt-1 min-h-20"
                            />
                          </div>
                          <Button 
                            onClick={handleBookCall} 
                            disabled={bookingLoading}
                            className="w-full bg-gradient-to-r from-violet-600 to-emerald-600 hover:from-violet-700 hover:to-emerald-700 py-6"
                          >
                            {bookingLoading ? (
                              <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Booking...</>
                            ) : (
                              <><Sparkles className="h-4 w-4 mr-2" />Confirm Pitch Booking</>
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Startups Grid */}
        {activeTab === 'startups' && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {loading ? (
              <div className="col-span-4 text-center py-20">
                <Loader2 className="h-10 w-10 animate-spin mx-auto text-emerald-500" />
              </div>
            ) : startups.length === 0 ? (
              <div className="col-span-4 text-center py-20">
                <Building2 className="h-16 w-16 mx-auto text-gray-700 mb-4" />
                <p className="text-gray-500">No startups featured yet</p>
              </div>
            ) : (
              startups.map((startup, idx) => (
                <Card key={startup.id} className="bg-gradient-to-br from-white/10 to-white/5 border-white/10 hover:border-emerald-500/50 transition-all group">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500 to-blue-500 flex items-center justify-center text-white font-bold shadow-lg">
                        {startup.name?.charAt(0) || 'S'}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-bold text-white group-hover:text-emerald-400 transition-colors">{startup.name}</h3>
                        <p className="text-gray-500 text-sm">{startup.industry}</p>
                      </div>
                    </div>
                    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 mb-3">
                      {startup.stage || 'Seed Stage'}
                    </Badge>
                    <p className="text-gray-400 text-sm line-clamp-2">{startup.description || 'Innovative startup ready for investment.'}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-white/10">
                      <span className="text-gray-500 text-xs flex items-center gap-1">
                        <Target className="h-3 w-3" />
                        Score: {startup.governance_score || Math.floor(Math.random() * 30 + 60)}%
                      </span>
                      <Button variant="ghost" size="sm" className="text-emerald-400 hover:text-emerald-300 p-0">
                        View <ArrowRight className="h-3 w-3 ml-1" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}
      </div>

      {/* Bottom CTA */}
      <div className="bg-gradient-to-r from-violet-900/50 to-emerald-900/50 border-t border-white/10">
        <div className="container mx-auto px-6 py-16 text-center">
          <Sparkles className="h-10 w-10 mx-auto mb-6 text-emerald-400" />
          <h2 className="text-3xl font-bold text-white mb-4">Ready to Make Your Pitch?</h2>
          <p className="text-gray-400 mb-8 max-w-xl mx-auto">
            Join the elite founders who've secured funding through SKI AXIS Pitch Day
          </p>
          <div className="flex items-center justify-center gap-4">
            <Button size="lg" className="bg-white text-slate-900 hover:bg-gray-100 px-8">
              <Play className="h-5 w-5 mr-2" />
              Watch Demo
            </Button>
            <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10 px-8">
              Learn More
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PitchDayPage;
